import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Button,
  Tooltip,
} from '@mui/material';

// Icônes MUI
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import AppPagination from '../../components/base/AppPagination';
import { usePagination } from '../../hooks/usePagination';
import AppSearch from '../../components/base/AppSearch';

// Sous-composants locaux (même dossier)
import OrderDialog from './OrderDialog';
import OrderDetailsDialog from './OrderDetailsDialog'; // 🟢 Remplacement de OrderDrawer par le Dialogue

const Commandes = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false); // 🟢 État pour ouvrir/fermer la modale de détails
  const [searchTerm, setSearchTerm] = useState('');

  const userRole = Number(user?.role || user?.role_user);
  const isIntervenant = userRole === 3;
  const isPraticien = userRole === 4;

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('token');

  // Gestion dynamique du rolePath selon le rôle (Admin [1,2], Praticien [4], Intervenant [3])
  const rolePath = useMemo(() => {
    if ([1, 2].includes(userRole)) return 'admin';
    if (userRole === 4) return 'praticien';
    return 'intervenant';
  }, [userRole]);

  const [openDialog, setOpenDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [offers, setOffers] = useState([]);
  const [workshops, setWorkshops] = useState([]);
  const [clients, setClients] = useState([]);
  const [intervenants, setIntervenants] = useState([]);

  // État du formulaire
  const [formData, setFormData] = useState({
    type_item: 'offre', // 'offre' ou 'atelier'
    id_intervenant: '',
    id_offre: '',
    id_ws: '',
    date_cmd: '',
    email_client: '',
    prix_libre: '',
  });

  const fetchOffers = useCallback(async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/${rolePath}/offer/g/lo/list_offer`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOffers(response.data.result || response.data.data || []);
    } catch (err) {
      console.error('Erreur offres:', err);
    }
  }, [apiUrl, rolePath, token]);

  const fetchWorkshops = useCallback(async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/${rolePath}/atelier/g/l/list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWorkshops(response.data.result || response.data.data || []);
    } catch (err) {
      console.error('Erreur ateliers:', err);
    }
  }, [apiUrl, rolePath, token]);

  const fetchClients = useCallback(async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/${rolePath}/clients/list/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClients(response.data.result || response.data.data || []);
    } catch (err) {
      console.warn(
        `Fallback clients: tentative via la route globale /admin/clients/list/all`
      );
      try {
        const fallbackRes = await axios.get(
          `${apiUrl}/admin/clients/list/all`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClients(fallbackRes.data.result || fallbackRes.data.data || []);
      } catch (fallbackErr) {
        console.error('Erreur finale chargement clients:', fallbackErr);
      }
    }
  }, [apiUrl, rolePath, token]);

  const fetchIntervenants = useCallback(async () => {
    if (isIntervenant || isPraticien) return;
    try {
      const response = await axios.get(
        `${apiUrl}/${rolePath}/intervenant/l/list_intervenant`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIntervenants(response.data.result || response.data.data || []);
    } catch (err) {
      console.error('Erreur intervenants:', err);
    }
  }, [apiUrl, rolePath, token, isIntervenant, isPraticien]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${apiUrl}/${rolePath}/offer/command/l/list/list_command`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOrders(response.data.result || response.data.data || []);
    } catch (err) {
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, rolePath]);

  useEffect(() => {
    if (user) {
      fetchOrders();
      fetchOffers();
      fetchWorkshops();
      fetchClients();
      fetchIntervenants();
    }
  }, [
    user,
    fetchOrders,
    fetchOffers,
    fetchWorkshops,
    fetchClients,
    fetchIntervenants,
  ]);

  const handleCreateOrder = async () => {
    const isAtelier = formData.type_item === 'atelier';
    const targetId = isAtelier ? formData.id_ws : formData.id_offre;

    if (!targetId || !formData.email_client) {
      toast.warning('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!isAtelier && !formData.date_cmd) {
      toast.warning('Veuillez sélectionner un créneau horaire pour la séance');
      return;
    }

    try {
      setSubmitting(true);

      let formattedDate = null;
      if (formData.date_cmd) {
        const dateObj = new Date(formData.date_cmd);
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const seconds = String(dateObj.getSeconds()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
      }

      const endpoint = isAtelier
        ? `${apiUrl}/${rolePath}/atelier/command/create/${targetId}`
        : `${apiUrl}/${rolePath}/offer/command/resevration/create_command/${targetId}`;

      await axios.post(
        endpoint,
        {
          start_time_reservation: formattedDate,
          email_client: formData.email_client,
          prix_libre: formData.prix_libre !== '' ? formData.prix_libre : null,
          type_item: formData.type_item,
          id_intervenant: formData.id_intervenant || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Commande et réservation créées avec succès !');
      setOpenDialog(false);
      setFormData({
        type_item: 'offre',
        id_intervenant: '',
        id_offre: '',
        id_ws: '',
        date_cmd: '',
        email_client: '',
        prix_libre: '',
      });
      fetchOrders();
    } catch (err) {
      const msg = err.response?.data?.message;
      toast.error(
        typeof msg === 'object'
          ? 'Erreur de validation des données'
          : msg || 'Erreur lors de la création'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      const searchStr = searchTerm.toLowerCase();
      const clientFull =
        `${o.client?.nom_client} ${o.client?.prenom_client}`.toLowerCase();
      return (
        o.ref_cmd?.toLowerCase().includes(searchStr) ||
        clientFull.includes(searchStr)
      );
    });
  }, [orders, searchTerm]);

  const itemsPerPage = 8;
  const { next, currentData, currentPage, count, totalItems } = usePagination(
    filteredOrders,
    itemsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  return (
    <Box sx={{ pt: { xs: '80px', md: '20px' }, px: 3, pb: 4 }}>
      {/* --- HEADER --- */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={{ mb: 4 }}
        spacing={2}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{ fontWeight: '800', color: '#2B3674', fontSize: '34px' }}
          >
            Gestion Commandes
          </Typography>
        </Box>
        <Stack
          direction="row"
          spacing={2}
          sx={{ width: { xs: '100%', md: 'auto' } }}
        >
          <AppSearch
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Référence, client..."
          />
          <Button
            variant="contained"
            onClick={() => setOpenDialog(true)}
            startIcon={<AddIcon />}
            sx={{
              bgcolor: '#4318FF',
              borderRadius: '20px',
              px: 8,
              py: 1.5,
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              boxShadow: '0px 4px 12px rgba(67, 24, 255, 0.2)',
              '&:hover': {
                bgcolor: '#3311CC',
                boxShadow: 'none',
              },
            }}
          >
            Nouvelle Commande
          </Button>
        </Stack>
      </Stack>

      {/* --- TABLEAU --- */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '20px',
          border: '1px solid #F4F7FE',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: '#F4F7FE' }}>
              <TableRow>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  RÉFÉRENCE
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  CLIENT / PROFIL
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  INTERVENANT / PRATICIEN
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  COMMISSION
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  PRODUIT
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  TOTAL TTC
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  PRIX SPÉCIAL SOA
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  PRIX LIBRE
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>MONTANT DÛ PRATICIEN</span>
                    <Tooltip
                      title="Calculé après déduction de la commission (uniquement pour les clients non-patients)"
                      arrow
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: '14px',
                          color: '#4318FF',
                          cursor: 'pointer',
                        }}
                      />
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  STATUT
                </TableCell>
                <TableCell sx={{ color: '#A3AED0', fontWeight: 700 }}>
                  DATE
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: '#A3AED0', fontWeight: 700 }}
                >
                  ACTION
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <CircularProgress
                      size={30}
                      sx={{ my: 4, color: '#4318FF' }}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                currentData().map((order) => {
                  const assignedUser = order.user || order.intervenant;
                  const isPatient = Number(order.client?.is_patient) === 1;

                  return (
                    <TableRow key={order.id_cmd} hover>
                      <TableCell sx={{ color: '#2B3674', fontWeight: 700 }}>
                        {order.ref_cmd}
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.5}>
                          <Typography
                            sx={{
                              color: '#2B3674',
                              fontWeight: 600,
                              fontSize: '14px',
                            }}
                          >
                            {isIntervenant || isPraticien
                              ? order.client?.prenom_client
                              : `${order.client?.prenom_client || ''} ${order.client?.nom_client || ''}`}
                          </Typography>
                          <Box>
                            <Chip
                              icon={
                                isPatient ? (
                                  <LocalHospitalIcon
                                    sx={{ fontSize: '12px !important' }}
                                  />
                                ) : (
                                  <PersonIcon
                                    sx={{ fontSize: '12px !important' }}
                                  />
                                )
                              }
                              label={isPatient ? 'Patient' : 'Client'}
                              size="small"
                              sx={{
                                height: '20px',
                                fontSize: '10px',
                                fontWeight: 700,
                                bgcolor: isPatient ? '#EEF2FF' : '#F4F7FE',
                                color: isPatient ? '#4318FF' : '#A3AED0',
                                '& .MuiChip-icon': {
                                  color: 'inherit',
                                },
                              }}
                            />
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell sx={{ color: '#4318FF', fontWeight: 600 }}>
                        {assignedUser ? (
                          <>
                            {assignedUser.nom_user} {assignedUser.prenom_user}
                          </>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>

                      <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                        {order.taux_commission !== undefined &&
                        order.taux_commission !== null
                          ? `${order.taux_commission}%`
                          : '—'}
                      </TableCell>

                      <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                        {order.details_commandes?.[0]?.label_offre_detcmd ||
                          'Pack Video'}
                      </TableCell>
                      <TableCell sx={{ color: '#2B3674', fontWeight: 800 }}>
                        {order.total_ttc_cmd}€
                      </TableCell>
                      <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                        {isPatient && order.prix_special_soa
                          ? `${order.prix_special_soa}€`
                          : '—'}
                      </TableCell>
                      <TableCell sx={{ color: '#2B3674', fontWeight: 600 }}>
                        {order.prix_libre ? `${order.prix_libre}€` : '—'}
                      </TableCell>

                      <TableCell>
                        <Stack spacing={0.2}>
                          <Typography
                            sx={{
                              color: '#2B3674',
                              fontWeight: 700,
                              fontSize: '14px',
                            }}
                          >
                            {order.montant_soa_du_au_praticien
                              ? `${order.montant_soa_du_au_praticien}€`
                              : '—'}
                          </Typography>
                          {!isPatient && (
                            <Typography
                              sx={{
                                color: '#A3AED0',
                                fontSize: '10px',
                                fontWeight: 500,
                                fontStyle: 'italic',
                              }}
                            >
                              (après commission)
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={
                            order.etat_cmd === 'SUCCESS' ? 'Payé' : 'En attente'
                          }
                          size="small"
                          sx={{
                            bgcolor:
                              order.etat_cmd === 'SUCCESS'
                                ? '#E6F9F0'
                                : '#FFF5F5',
                            color:
                              order.etat_cmd === 'SUCCESS'
                                ? '#05CD99'
                                : '#EE5D50',
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: '#2B3674' }}>
                        {formatDate(order.created_at)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          onClick={() => {
                            setSelectedOrder(order);
                            setOpenDetailsModal(true); // 🟢 Ouvre la modale de détails au clic
                          }}
                          sx={{ color: '#4318FF', bgcolor: '#F4F7FE' }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <AppPagination
          count={count}
          page={currentPage}
          onChange={next}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
        />
      </Paper>

      {/* --- DIALOGUE FORMULAIRE (Création) --- */}
      <OrderDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        offers={offers}
        workshops={workshops}
        clients={clients}
        intervenants={intervenants}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleCreateOrder}
        submitting={submitting}
      />

      {/* --- MODALE DÉTAILS DE LA COMMANDE --- */}
      <OrderDetailsDialog
        open={openDetailsModal}
        onClose={() => setOpenDetailsModal(false)}
        selectedOrder={selectedOrder}
        isIntervenant={isIntervenant || isPraticien}
        apiUrl={apiUrl}
      />
    </Box>
  );
};

export default Commandes;
